import MultipleChoiceQuestion from '@de/components/Step/variants/MultipleChoiceQuestion';
import React from 'react';
import { GroupedQuestions, Option, Question, Lead } from '@de/types/types';
import RadioQuestion from '@de/components/Step/variants/RadioQuestion';
import DynamicSingleChoiceQuestion from '@de/components/Step/variants/DynamicSingleChoiceQuestion';
import DynamicMultipleChoiceQuestion from '@de/components/Step/variants/DynamicMultipleChoiceQuestion';
import TextQuestion from '@de/components/Step/variants/TextQuestion';
import DropdownQuestion from '@de/components/Step/variants/Dropdown';
import Toggle from '@de/components/Step/variants/Toggle';
import MultiQuestion from '@de/components/Step/variants/MultiQuestion';
import CheckboxQuestion from '@de/components/Step/variants/CheckboxQuestion';
import SingleChoiceQuestion from '@de/components/Step/variants/SingleChoiceQuestion';
import { ResultType } from '@de/types/enums';

export interface QuestionProps {
  options?: Option[];
  isOptionsLoading?: boolean;
  /**
   * Full custom-endpoint option set when "Enable Search Bar Options" is on and
   * a `limit` is configured. `options` holds the first `limit` for inline
   * display; the static-choice variants derive the searchable remainder
   * (everything not already displayed) and feed it to the hen-ui `Combobox`.
   * `undefined` when the search bar is off or no `limit` is set.
   */
  searchBarAllOptions?: Option[];
  /**
   * Set when dynamic options fail to load (e.g. a settled cache-miss fallback
   * fetch throws). Surfaced by the DYNAMIC_* variants, MULTI_QUESTION children,
   * and the standalone static-choice variants (MULTIPLE_CHOICE / SINGLE_CHOICE
   * / SELECT), which render it via the shared `ErrorMessage` and suppress the
   * loading placeholder so a failed fetch no longer falls silently to an empty
   * state.
   */
  dynamicOptionsError?: string;
  className?: string;
  variant?: string;
  isEmbeddedVoyagerActive?: boolean;
  leadFieldAssociated?: keyof Lead;
  id?: string;
  parentQuestionId?: string;
  questionType?: string;
  isMultiQuestionChild?: boolean | undefined;
  type?: string;
  title?: string;
  stepName?: string;
  hasValidationError?: boolean;
  tooltip?: string;
  maxSelection?: number;
  placeholderText?: string;
  automaticNext?: boolean;
  dynamicOptionsFilters?: {
    type?: string;
    subject?: string;
    filterKey?: 'types' | 'subTypes' | 'subjects' | 'skills';
  };
  showOrderNumber?: boolean;
}

export interface MultiQuestionProps {
  id?: string;
  stepQuestions?: Question[];
  bulletPoints?: string[];
  enableDivider?: boolean;
  hideNext?: boolean;
  showQDF?: boolean;
  groupedQuestions?: GroupedQuestions[];
  automaticNext?: boolean;
  tcpaText?: string;
}

export interface ComponentProps {
  componentType?: string;
  redirectUrl?: string;
}

interface ResultProps {
  id?: string;
  resultType?: `${ResultType}`;
}

export type StepProps = QuestionProps &
  MultiQuestionProps &
  ComponentProps &
  ResultProps;

const Step: React.FC<StepProps> = (props) => {
  switch (props.questionType || props.type) {
    case 'RADIO_CHOICE':
      return <RadioQuestion {...props} />;
    case 'DYNAMIC_SINGLE_CHOICE':
      return <DynamicSingleChoiceQuestion {...props} />;
    case 'DYNAMIC_MULTIPLE_CHOICE':
      return <DynamicMultipleChoiceQuestion {...props} />;
    case 'TOGGLE':
      return <Toggle {...props} />;
    case 'MULTIPLE_CHOICE':
      return <MultipleChoiceQuestion showOrderNumber {...props} />;
    case 'SINGLE_CHOICE':
      return <SingleChoiceQuestion {...props} />;
    case 'CHECKBOX':
      return <CheckboxQuestion {...props} />;
    case 'TEXT':
      return <TextQuestion {...props} />;
    case 'SELECT':
      return <DropdownQuestion {...props} />;
    case 'MULTI_QUESTION':
      return <MultiQuestion {...props} />;
    default:
      return <MultipleChoiceQuestion {...props} />;
  }
};

export default Step;
